import { t as localeText } from "@/shared/lib/translate";
import { useTranslation as useLocaleSubscription } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { UploadIcon } from "./icons";

/** One active composer accepts external files anywhere on its workspace. */
export function WorkspaceFileDrop({ enabled = true, disabled = false, label, onFiles }: {
  enabled?: boolean; disabled?: boolean; label: string; onFiles: (files: File[]) => void;
}) {
  useLocaleSubscription();
  const anchor = useRef<HTMLSpanElement>(null);
  const latest = useRef({ disabled, onFiles });
  latest.current = { disabled, onFiles };
  const [dragging, setDragging] = useState(false);
  useEffect(() => {
    if (!enabled) { setDragging(false); return; }
    let depth = 0;
    const available = (event: DragEvent) => !anchor.current?.closest('[hidden], [inert]')
      && !((event.target as Element | null)?.closest?.('[role="dialog"][aria-modal="true"]'))
      && Array.from(event.dataTransfer?.types ?? []).includes("Files");
    const enter = (event: DragEvent) => { if (!available(event)) return; event.preventDefault(); depth++; setDragging(true); };
    const over = (event: DragEvent) => { if (!available(event)) return; event.preventDefault(); if (event.dataTransfer) event.dataTransfer.dropEffect = latest.current.disabled ? "none" : "copy"; };
    const leave = (event: DragEvent) => { if (!available(event)) return; depth = Math.max(0, depth - 1); if (!depth) setDragging(false); };
    const reset = () => { depth = 0; setDragging(false); };
    const drop = (event: DragEvent) => {
      if (!available(event)) { reset(); return; }
      event.preventDefault(); event.stopImmediatePropagation(); reset();
      if (!latest.current.disabled) latest.current.onFiles(Array.from(event.dataTransfer?.files ?? []));
    };
    document.addEventListener("dragenter", enter, true);
    document.addEventListener("dragover", over, true);
    document.addEventListener("dragleave", leave, true);
    document.addEventListener("drop", drop, true);
    window.addEventListener("blur", reset);
    window.addEventListener("dragend", reset);
    return () => {
      document.removeEventListener("dragenter", enter, true); document.removeEventListener("dragover", over, true);
      document.removeEventListener("dragleave", leave, true); document.removeEventListener("drop", drop, true);
      window.removeEventListener("blur", reset); window.removeEventListener("dragend", reset);
    };
  }, [enabled]);
  return <><span ref={anchor} className="sr-only" />{dragging && createPortal(<div className="studio-workspace-drop-overlay" aria-hidden="true">
    <div><UploadIcon /><span>{disabled ? localeText("interface.finish_the_current_action_before_adding_media_3e68cf70", { defaultValue: "Finish the current action before adding media" }) : label}</span></div>
  </div>, document.body)}</>;
}
